import useApi from "../services/ApiService";
import { Paging } from "../models/Paging";
import { MigrationType } from "../models/Statistics/MigrationType";
import {
  FlaggedPathOccurrence,
  FlaggedPathReason,
  FlaggedPathStatus,
  FlaggedPathSummary,
  UpdateFlaggedPathRequest,
} from "../models/FlaggedPath";

export interface GetFlaggedPathsOptions {
  status?: FlaggedPathStatus | null;
  recurred?: boolean | null;
  migrationType?: MigrationType | null;
  reason?: FlaggedPathReason | null;
  sourceTable?: string | null;
  sourceRecordId?: string | null;
  /** One entity's business key, matched whole. Scopes the list to that entity; `search` cannot, being a fragment match. */
  migrationEntityId?: string | null;
  search?: string | null;
  pageNumber?: number | null;
  pageSize?: number | null;
  descending?: boolean;
}

export interface GetFlaggedPathOccurrencesOptions {
  status?: FlaggedPathStatus | null;
  migrationType?: MigrationType | null;
  /** Narrow to the records one entity owns. One entity reaches a folder once per source record, so this is still a list. */
  migrationEntityId?: string | null;
  pageNumber?: number | null;
  pageSize?: number | null;
}

export class FlaggedPathsRepository {
  private api = useApi();

  async getFlaggedPaths(
    organizationId: string,
    options: GetFlaggedPathsOptions = {}
  ): Promise<{ data: FlaggedPathSummary[]; paging: Paging | null }> {
    const {
      // Null is dropped from the query string, which the server reads as no filter. A page's own default is
      // expressed by the caller passing a value, never by a default here.
      status = null,
      recurred = null,
      migrationType = null,
      reason = null,
      sourceTable = null,
      sourceRecordId = null,
      migrationEntityId = null,
      search = null,
      pageNumber = null,
      pageSize = null,
      descending = true,
    } = options;

    const response = await this.api.get(`/api/Organizations/${organizationId}/flagged-paths`, {
      params: {
        Status: status,
        Recurred: recurred,
        MigrationType: migrationType,
        Reason: reason,
        SourceTable: sourceTable,
        SourceRecordId: sourceRecordId,
        MigrationEntityId: migrationEntityId,
        Search: search,
        PageNumber: pageNumber,
        PageSize: pageSize,
        Descending: descending,
      },
    });

    return { data: response.data, paging: readPaging(response.headers) };
  }

  /** The source records behind one path, fetched when its row is expanded rather than inlined in the list. */
  async getFlaggedPathOccurrences(
    organizationId: string,
    flaggedPathId: string,
    options: GetFlaggedPathOccurrencesOptions = {}
  ): Promise<{ data: FlaggedPathOccurrence[]; paging: Paging | null }> {
    const {
      status = null,
      migrationType = null,
      migrationEntityId = null,
      pageNumber = null,
      pageSize = null,
    } = options;

    const response = await this.api.get(
      `/api/Organizations/${organizationId}/flagged-paths/${flaggedPathId}/occurrences`,
      {
        params: {
          Status: status,
          MigrationType: migrationType,
          MigrationEntityId: migrationEntityId,
          PageNumber: pageNumber,
          PageSize: pageSize,
        },
      }
    );

    return { data: response.data, paging: readPaging(response.headers) };
  }

  async updateFlaggedPath(
    organizationId: string,
    flaggedPathId: string,
    flaggedPath: UpdateFlaggedPathRequest
  ): Promise<void> {
    await this.api.patch(`/api/Organizations/${organizationId}/flagged-paths/${flaggedPathId}`, flaggedPath);
  }
}

// Kept local rather than shared with SourceLinksRepository: both read the same header the pagination filter
// writes, and neither owns a home for it that the other could import without coupling two unrelated pages.
function readPaging(headers: Record<string, unknown>): Paging | null {
  const header = headers["x-pagination"];
  if (!header || typeof header !== "string") return null;

  try {
    return JSON.parse(header) as Paging;
  } catch {
    return null;
  }
}

export default FlaggedPathsRepository;
